/**
 * FiltroPeriodo — dropdown de período (data de criação) com presets rápidos
 * (Hoje, 7/30 dias, Este mês) + intervalo customizado de/até. Aplicação ao
 * vivo; `valor` = { de, ate } com strings "YYYY-MM-DD" ou null (sem filtro).
 */
import React, { useEffect, useRef, useState } from "react";
import { FiCalendar, FiChevronDown } from "react-icons/fi";

import {
  FexBadge,
  FexBtn,
  FexFooter,
  FexPanel,
  FexWrap,
  Input,
  Label,
} from "./styles";

function isoLocal(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(
    d.getDate(),
  ).padStart(2, "0")}`;
}

const PRESETS = [
  {
    label: "Hoje",
    calc: () => {
      const hoje = isoLocal(new Date());
      return { de: hoje, ate: hoje };
    },
  },
  {
    label: "Últimos 7 dias",
    calc: () => {
      const fim = new Date();
      const ini = new Date();
      ini.setDate(fim.getDate() - 6);
      return { de: isoLocal(ini), ate: isoLocal(fim) };
    },
  },
  {
    label: "Últimos 30 dias",
    calc: () => {
      const fim = new Date();
      const ini = new Date();
      ini.setDate(fim.getDate() - 29);
      return { de: isoLocal(ini), ate: isoLocal(fim) };
    },
  },
  {
    label: "Este mês",
    calc: () => {
      const agora = new Date();
      return {
        de: isoLocal(new Date(agora.getFullYear(), agora.getMonth(), 1)),
        ate: isoLocal(agora),
      };
    },
  },
];

function dataBr(iso) {
  if (!iso) return "";
  const [y, m, d] = iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
}

export default function FiltroPeriodo({ valor, onChange }) {
  const [aberto, setAberto] = useState(false);
  // Painel alinhado à direita quando não cabe até a borda da viewport.
  const [alinhaDireita, setAlinhaDireita] = useState(false);
  const wrapRef = useRef(null);
  const ativo = Boolean(valor?.de || valor?.ate);

  const abrirFechar = () => {
    const r = wrapRef.current?.getBoundingClientRect();
    setAlinhaDireita(Boolean(r && r.left + 290 > window.innerWidth));
    setAberto((v) => !v);
  };

  useEffect(() => {
    if (!aberto) return undefined;
    const aoClicarFora = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) {
        setAberto(false);
      }
    };
    const aoTeclar = (e) => {
      if (e.key === "Escape") setAberto(false);
    };
    document.addEventListener("mousedown", aoClicarFora);
    document.addEventListener("keydown", aoTeclar);
    return () => {
      document.removeEventListener("mousedown", aoClicarFora);
      document.removeEventListener("keydown", aoTeclar);
    };
  }, [aberto]);

  const resumo = ativo
    ? `${dataBr(valor.de) || "…"} – ${dataBr(valor.ate) || "…"}`
    : "";

  return (
    <FexWrap ref={wrapRef}>
      <FexBtn
        type="button"
        $ativo={aberto || ativo}
        onClick={abrirFechar}
        aria-expanded={aberto}
      >
        <FiCalendar size={13} /> Período
        {ativo && <FexBadge title={resumo}>1</FexBadge>}
        <FiChevronDown size={13} />
      </FexBtn>

      {aberto && (
        <FexPanel
          aria-label="Filtro de período"
          style={{
            minWidth: 270,
            ...(alinhaDireita ? { left: "auto", right: 0 } : {}),
          }}
        >
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, padding: "10px 10px 4px" }}>
            {PRESETS.map((p) => (
              <FexBtn
                key={p.label}
                type="button"
                style={{ height: 28, fontSize: 12 }}
                onClick={() => onChange(p.calc())}
              >
                {p.label}
              </FexBtn>
            ))}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8, padding: "6px 10px 10px" }}>
            <div>
              <Label htmlFor="filtro-periodo-de">De</Label>
              <Input
                id="filtro-periodo-de"
                type="date"
                value={valor?.de || ""}
                onChange={(e) =>
                  onChange({ de: e.target.value || null, ate: valor?.ate || null })
                }
              />
            </div>
            <div>
              <Label htmlFor="filtro-periodo-ate">Até</Label>
              <Input
                id="filtro-periodo-ate"
                type="date"
                value={valor?.ate || ""}
                onChange={(e) =>
                  onChange({ de: valor?.de || null, ate: e.target.value || null })
                }
              />
            </div>
          </div>
          <FexFooter>
            <span style={{ fontSize: 11.5, color: "var(--ink-3)" }}>
              {ativo ? resumo : "Sem período aplicado"}
            </span>
            <button
              type="button"
              disabled={!ativo}
              onClick={() => onChange({ de: null, ate: null })}
            >
              Limpar
            </button>
          </FexFooter>
        </FexPanel>
      )}
    </FexWrap>
  );
}
